/**
 * An anchor's tier card, as data (APP-ENTITLEMENT-001 Phase 11): the public catalog's anchor row → the `TierData`
 * `<PricingTable>` renders. The headline is the anchor's first size dimension's published price for the chosen
 * cycle (annual = ten months, Q-AE-26); the note carries its other price sentences (the register beyond the three
 * included); the features are what the anchor includes and the apps that come free with it; the CTA is the ONE
 * signup URL. An anchor marked "coming soon" has no price, no CTA and a badge.
 *
 * @module @puffin-core/marketing-shell/pricing/anchorTiers
 */

import {
  BILLING_CYCLE,
  DIMENSION_KEY,
  annualFromMonthly,
  freeEntriesOf,
  isPriced,
  monthlyUnitPriceOf,
  sizeDimensionsOf,
} from '@puffin-core/app-catalog';
import { featureKeyOf, priceLinesOf, unitKeyOf } from '@puffin-core/account-apps-ui/copy';
import { BADGE_TONE } from '@puffin-core/status-taxonomy';

import { signupUrlFor } from '../registries/signupUrl.js';
import { ANNUAL_FREE_MONTHS } from './pricingRules.js';

/** @typedef {import('./pricingRules.js').PublicCatalog} PublicCatalog */

/**
 * A translator, as the copy rules take one: read off `priceLinesOf`'s own signature, never retyped here.
 *
 * @typedef {Parameters<typeof priceLinesOf>[0]['t']} Translate
 */

/**
 * The headline price of an anchor: amount, period and (annual) billing line.
 *
 * @param {Object} params
 * @param {Object} params.entry
 * @param {string} params.billingCycle
 * @param {Translate} params.t - the `marketingShell` translator.
 * @param {Translate} params.tApps - the `accountAppsUi` translator.
 * @param {(cents: number) => string} params.formatPrice
 * @returns {{amount: string, period: string, billing?: string}}
 */
export function headlinePriceOf({ entry, billingCycle, t, tApps, formatPrice }) {
  const [first] = sizeDimensionsOf(entry);
  const monthlyCents = monthlyUnitPriceOf(first);
  const isFlat = first.dimensionKey === DIMENSION_KEY.ACCOUNT;
  const unit = isFlat ? null : tApps(unitKeyOf(first.dimensionKey));
  if (billingCycle === BILLING_CYCLE.ANNUAL) {
    return {
      amount: formatPrice(annualFromMonthly(monthlyCents)),
      period: isFlat ? t('pricing.flatYear') : t('pricing.perUnitYear', { unit }),
      billing: t('pricing.annualNote', {
        monthly: formatPrice(monthlyCents),
        free: String(ANNUAL_FREE_MONTHS),
      }),
    };
  }
  return {
    amount: formatPrice(monthlyCents),
    period: isFlat ? t('pricing.flatMonth') : t('pricing.perUnitMonth', { unit }),
  };
}

/**
 * @param {Object} params
 * @param {PublicCatalog} params.catalog
 * @param {Object} params.entry - an anchor's public row.
 * @param {string} params.siteAppKey - the app whose site this is (the CTA names it beside the anchor).
 * @param {string} params.featuredAnchorKey
 * @param {string} params.billingCycle
 * @param {string} [params.fromOrigin]
 * @param {Translate} params.t
 * @param {Translate} params.tApps
 * @param {(cents: number) => string} params.formatPrice
 * @returns {Object} a `TierData` for `<PricingTable>`.
 */
export function anchorTierOf({
  catalog,
  entry,
  siteAppKey,
  featuredAnchorKey,
  billingCycle,
  fromOrigin,
  t,
  tApps,
  formatPrice,
}) {
  const features = [
    ...entry.capabilityKeys.map((key) => ({ key, text: tApps(featureKeyOf(key)) })),
    ...freeEntriesOf(catalog, entry.appKey).map((app) => ({
      key: `free-${app.appKey}`,
      text: t('pricing.freeAppFeature', { app: app.label }),
    })),
  ];
  const base = {
    key: entry.appKey,
    name: entry.label,
    currency: '',
    features,
    featured: entry.appKey === featuredAnchorKey,
  };
  if (entry.comingSoon || !entry.isSellable || !isPriced(entry)) {
    return {
      ...base,
      amount: t('pricing.comingSoon'),
      period: '',
      badge: { label: t('pricing.comingSoon'), tone: BADGE_TONE.NEUTRAL },
    };
  }
  const [, ...moreLines] = priceLinesOf({ entry, t: tApps, formatPrice });
  return {
    ...base,
    ...headlinePriceOf({ entry, billingCycle, t, tApps, formatPrice }),
    ...(moreLines.length > 0 ? { note: moreLines.join(' · ') } : {}),
    cta: {
      label: t('pricing.startTrial'),
      to: signupUrlFor({
        anchor: entry.appKey,
        ...(siteAppKey === entry.appKey ? {} : { app: siteAppKey }),
        fromOrigin,
      }),
    },
  };
}
